import React from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { getStripe } from '../utils/stripe';

interface Props {
  /** Returns the Checkout Session client_secret from our API. */
  fetchClientSecret: () => Promise<string>;
  onClose: () => void;
  title?: string;
}

/**
 * In-site Stripe checkout. Renders Stripe's embedded Checkout inside a deepnets
 * modal so the customer never leaves deepnets.ai — no jarring redirect to the
 * Stripe-hosted "Percipio LLC" page. On completion, Stripe navigates the page
 * to the return_url (also on deepnets.ai) handled by CheckoutReturnPage.
 */
const EmbeddedCheckoutModal: React.FC<Props> = ({ fetchClientSecret, onClose, title = 'Checkout' }) => {
  const stripePromise = getStripe();
  if (!stripePromise) return null; // key not configured — caller should fall back to redirect

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-crypto-gray border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm font-semibold text-white">{title}</span>
          <button
            onClick={onClose}
            aria-label="Close checkout"
            className="text-gray-400 hover:text-white w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-800 transition-colors"
          >
            <i className="fas fa-times" />
          </button>
        </div>
        {/* Stripe injects an iframe here; it sizes itself. bg-white so the
            embedded form (light) sits on a clean surface. */}
        <div className="p-2 bg-white rounded-b-2xl min-h-[360px]">
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedCheckoutModal;
